import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { handleErrorResponse, forceReload } from "../utils/util"
import {
  GenAuthService, LoginService, LogoutService, GetCurrentUserService,
  ResetPasswordService, ResetPasswordConfirmService, GetUserInitInfoService, StaffRegisterService,
  ControlService, RecordUserAccessService, ResetPasswordConfirmRetrieveData
} from '../api'
import { resetAppState } from "../store/common";
import i18n from 'i18next';

interface StaffInfo {
    email: string,
    first_name: string,
    last_name: string,
    type: string,
    can_man_talent: boolean,
    can_man_action: boolean,
    can_man_job: boolean,
    can_man_news: boolean,
    avatar: string,
    staff_type_display: [],
    is_allow_ip: boolean,
}
interface TalentInfo {
    email: string,
    first_name: string,
    last_name: string,
}
interface SuperUserInfo {
    id: number | null,
    email: string,
    first_name: string,
    last_name: string,
}
const initialState = {
    name: undefined,
    email: undefined,
    staffInfo: undefined as undefined | StaffInfo,
    talentInfo: undefined as undefined | TalentInfo,
    editingEmail: undefined,
    editingPassword: undefined,
    initStaffInfo: undefined as undefined | StaffInfo,
    isLoading: false,
    isAuthCodeCreated: false,
    isResetPwEmailSent: false,
    isResetPwCompleted: false,
    showPassword: false,
    error: undefined,
    errors: {},
    socketInstance: null,
    staffPermission: {},
    staffDefault: {},
    superUserInfo: undefined as undefined | SuperUserInfo,
    resMessage: '',
    superUserToast: { message: '', variant: 'success' },
    passCheckCode: "checking", // 'checking' | 'passed' | 'failed'
}

async function refreshCsrfToken(token) {
    const input = document.querySelector<HTMLInputElement>(
        '[name=csrfmiddlewaretoken]'
    );
    if (input) {
        input.value = token;
    }
    return;
}

const genAuthCodeUser = createAsyncThunk(
    'genAuthCodeUser',
    async ({email, password, view, navigate}: any, thunkAPI: any) => {
        const trimEmail = email.trim()
        thunkAPI.dispatch(currentUserActions.setIsLoading(true))
        const res = await GenAuthService.genAuthCreate({requestBody:{username: trimEmail, password, view}}) as any
        thunkAPI.dispatch(currentUserActions.setIsLoading(false))
        if (res.success) {
            thunkAPI.dispatch(currentUserActions.setEditingEmail(trimEmail))
            thunkAPI.dispatch(currentUserActions.setEditingPassword(password))
            thunkAPI.dispatch(currentUserActions.setIsAuthCodeCreated(true))
            return
        }
        if (res.email) {
            // reset state
            thunkAPI.dispatch(resetAppState());

            // force reload
            const currentUserState = thunkAPI.getState().currentUser;
            forceReload(res?.id, currentUserState);

            thunkAPI.dispatch(currentUserActions.setInitState())
            thunkAPI.dispatch(currentUserActions.setEmail(res.email))
            if (res.staff) {
              res.staff.is_allow_ip = true  // Allow on login
            }
            thunkAPI.dispatch(currentUserActions.setStaffInfo(res.staff))
            thunkAPI.dispatch(currentUserActions.setTalentInfo(res.talent))
            if (view == 'company')
                navigate("/company")
            else if (view == 'talent')
                navigate("/talent")
            return
        }
        thunkAPI.dispatch(currentUserActions.setInitState())
        thunkAPI.dispatch(currentUserActions.setError(res.message))
    },
)
const resetPassword = createAsyncThunk(
    'resetPassword',
    async ({email, view}: any, thunkAPI: any) => {
        const trimEmail = email.trim()
        thunkAPI.dispatch(currentUserActions.setInitState())
        thunkAPI.dispatch(currentUserActions.setIsLoading(true))
        const res = await ResetPasswordService.resetPasswordCreate({requestBody:{username: trimEmail, view}}) as any
        thunkAPI.dispatch(currentUserActions.setIsLoading(false))
        thunkAPI.dispatch(currentUserActions.setIsResetPwEmailSent(true))
        if (res.success) {
            thunkAPI.dispatch(currentUserActions.setEditingEmail(trimEmail))
            return
        }
        thunkAPI.dispatch(currentUserActions.setError(res.message))
    },
)
const resetPasswordConfirm = createAsyncThunk(
    'resetPasswordConfirm',
    async ({navigate, view, reset_password_code, password}: any, thunkAPI: any) => {
        thunkAPI.dispatch(currentUserActions.setInitState())
        thunkAPI.dispatch(currentUserActions.setIsLoading(true))
        const res = await ResetPasswordConfirmService.resetPasswordConfirmCreate({requestBody:{view, reset_password_code, password}}) as any
        thunkAPI.dispatch(currentUserActions.setIsLoading(false))
        if (res.email) {
            thunkAPI.dispatch(currentUserActions.setIsResetPwCompleted(true))
            if (view == 'talent')
                return
            if (view == 'company') {
                navigate("/company/login")
                return
            }
        }
        thunkAPI.dispatch(currentUserActions.setError(res.message))
    },
)
const signInUser = createAsyncThunk(
    'signInUser',
    async ({navigate, view, auth_code, remember}: any, thunkAPI: any) => {
        const {editingEmail, editingPassword} = thunkAPI.getState().currentUser
        thunkAPI.dispatch(currentUserActions.setIsLoading(true))
        const res = await LoginService.loginCreate({requestBody:{username: editingEmail, password: editingPassword, auth_code, view, remember}}) as any
        thunkAPI.dispatch(currentUserActions.setIsLoading(false))
        if (res.email) {
            // reset state
            thunkAPI.dispatch(resetAppState());

            // force reload
            const currentUserState = thunkAPI.getState().currentUser;
            forceReload(res?.id, currentUserState);

            thunkAPI.dispatch(currentUserActions.setInitState())
            thunkAPI.dispatch(currentUserActions.setEmail(res.email))
            if (res.staff) {
              res.staff.is_allow_ip = true  // Allow on login
            }
            thunkAPI.dispatch(currentUserActions.setStaffInfo(res.staff))
            thunkAPI.dispatch(currentUserActions.setTalentInfo(res.talent))
            if (view == 'company')
                navigate("/company")
            else if (view == 'talent')
                navigate("/talent")
            return
        }
        thunkAPI.dispatch(currentUserActions.setError(res.message))
    },
)
const signOutUser = createAsyncThunk(
    'signOutUser',
    async ({navigate, view}: any, thunkAPI: any) => {
        try {
            const res = await LogoutService.logoutCreate()
            if (res.success) {
                // reset state
                thunkAPI.dispatch(resetAppState());

                thunkAPI.dispatch(currentUserActions.setInitState())
                if (view == 'company')
                    navigate("/company/login")
                else if (view == 'talent')
                    navigate("/talent/login")
                else if (view == 'control')
                    navigate("/control")
                return
            }
        } catch (error: any) {
            return handleErrorResponse(error, view, thunkAPI);
        }
    },
)
const getCurrentUser = createAsyncThunk(
    'getCurrentUser',
    async ({navigate, view}: any , thunkAPI: any) => {
        try {
            const res = await GetCurrentUserService.getCurrentUserRetrieve() as any
            if (res.email) {
                //refresh CSRF-Token
                refreshCsrfToken(res.csrfToken)
                thunkAPI.dispatch(currentUserActions.setEmail(res.email))
                thunkAPI.dispatch(currentUserActions.setStaffInfo(res.staff))
                thunkAPI.dispatch(currentUserActions.setTalentInfo(res.talent))
                thunkAPI.dispatch(currentUserActions.setSuperUserInfo(res.super_user))
                thunkAPI.dispatch(currentUserActions.setStaffPermission(res.permission))
                return
            }
        } catch (e) {
            if (view == 'company')
                navigate("/company/login")
            else if (view == 'talent')
                navigate("/talent/login")
            else if (view == 'control')
                navigate("/control")
            return
        }
    },
)
const getUserInitInfo = createAsyncThunk(
    'getUserInitInfo',
    async ({registerCode}: any, thunkAPI: any) => {
        const res = await GetUserInitInfoService.getUserInitInfoRetrieve({registerCode}) as any
        if (!res.id) return thunkAPI.dispatch(currentUserActions.setPassCheckCode("failed"))
        thunkAPI.dispatch(currentUserActions.setInitStaffInfo(res))
        return thunkAPI.dispatch(currentUserActions.setPassCheckCode("passed"))
    },
)
const staffRegister = createAsyncThunk(
    'staffRegister',
    async ({navigate, register_code, password, first_name, last_name}: any, thunkAPI: any) => {
        thunkAPI.dispatch(currentUserActions.setInitState())
        const res = await StaffRegisterService.staffRegisterCreate({requestBody:{register_code, password, first_name, last_name}}) as any
        if (res.email) {
            // reset state
            thunkAPI.dispatch(resetAppState());

            // force reload
            const currentUserState = thunkAPI.getState().currentUser;
            forceReload(res?.id, currentUserState);

            thunkAPI.dispatch(currentUserActions.setInitState())
            thunkAPI.dispatch(currentUserActions.setEmail(res.email))
            if (res.staff) {
              res.staff.is_allow_ip = true  // Allow on login
            }
            thunkAPI.dispatch(currentUserActions.setStaffInfo(res.staff))
            thunkAPI.dispatch(currentUserActions.setTalentInfo(res.talent))
            navigate("/company")
            return
        }
        thunkAPI.dispatch(currentUserActions.setError(res.message))
    },
)
const loginSuperUser = createAsyncThunk(
    'loginSuperUser',
    async ({navigate}: any, thunkAPI: any) => {
        thunkAPI.dispatch(currentUserActions.setIsLoading(true))
        try {
            const res = await ControlService.controlApiSuperUserLoginCreate() as any;
            if (res) {
                if(res.success) {
                    // reset state
                    thunkAPI.dispatch(resetAppState());

                    // force reload
                    const currentUserState = thunkAPI.getState().currentUser;
                    forceReload(res.data?.user_id, currentUserState);

                    thunkAPI.dispatch(currentUserActions.setSuperUserInfo(res.data))
                    navigate("/control/company")
                } else {
                    thunkAPI.dispatch(currentUserActions.setResMessage(res.message))
                }
            }
        } catch (error: any) {
            const errorMessage = error?.body?.message || i18n.t('MSG_GLOBAL_INTERNAL_SERVER_ERROR');
            thunkAPI.dispatch(currentUserActions.setResMessage(errorMessage));            
            return thunkAPI.rejectWithValue(errorMessage);
        } finally {
            thunkAPI.dispatch(currentUserActions.setIsLoading(false))
        }
    },
)
const checkResetPasswordConfirm = createAsyncThunk(
    'checkResetPasswordConfirm',
    async (data: ResetPasswordConfirmRetrieveData, thunkAPI: any) => {
        const res = await ResetPasswordConfirmService.resetPasswordConfirmRetrieve(data) as any
        if (res.success) return thunkAPI.dispatch(currentUserActions.setPassCheckCode("passed"))
        return thunkAPI.dispatch(currentUserActions.setPassCheckCode("failed"))
    },
)
const recordUserAccess = createAsyncThunk(
    'recordUserAccess',
    async ({urlPath}: any, thunkAPI: any) => {
        const res = await RecordUserAccessService.recordUserAccessCreate({ requestBody: {urlPath: urlPath} }) as any
        return;
    },
)
const currentUserSlice = createSlice({
    name: 'currentUser',
    initialState,
    reducers: {
        setIsLoading: (state, action) => {
            state.isLoading = action.payload
        },
        setEmail: (state, action) => {
            state.email = action.payload
        },
        setEditingEmail: (state, action) => {
            state.editingEmail = action.payload
        },
        setEditingPassword: (state, action) => {
            state.editingPassword = action.payload
        },
        setIsAuthCodeCreated: (state, action) => {
            state.isAuthCodeCreated = action.payload
        },
        setIsResetPwEmailSent: (state, action) => {
            state.isResetPwEmailSent = action.payload
        },
        setIsResetPwCompleted: (state, action) => {
            state.isResetPwCompleted = action.payload
        },
        setInitStaffInfo: (state, action) => {
            state.initStaffInfo = action.payload
        },
        setStaffInfo: (state, action) => {
            state.staffInfo = action.payload
        },
        setTalentInfo: (state, action) => {
            state.talentInfo = action.payload
        },
        setSuperUserInfo: (state, action) => {
            state.superUserInfo = action.payload
        },
        switchShowPassword: (state) => {
            state.showPassword = !state.showPassword
        },
        setError: (state, action) => {
            state.error = action.payload
        },
        setErrors: (state, action) => {
            state.errors = action.payload
        },
        setInitState: (state) => {
            return initialState
        },
        setSocketInstance: (state, action) => {
            state.socketInstance = action.payload
        },
        setStaffPermission: (state, action) => {
            state.staffPermission = action.payload
        },
        setStaffDefault: (state, action) => {
            state.staffDefault = action.payload
        },
        setResMessage: (state, action) => {
            state.resMessage = action.payload
        },
        setSuperUserToast: (state, action) => {
            state.superUserToast = action.payload
        },
        setPassCheckCode: (state, action) => {
            state.passCheckCode = action.payload
        },
    },
    extraReducers: (builder) => {
        builder.addCase(resetAppState, () => initialState);
    }
})
const currentUserActions = {
    ...currentUserSlice.actions,
    genAuthCodeUser,
    resetPassword,
    resetPasswordConfirm,
    signInUser,
    signOutUser,
    getCurrentUser,
    getUserInitInfo,
    staffRegister,
    loginSuperUser,
    checkResetPasswordConfirm,
    recordUserAccess,
}
export {currentUserActions}
export default currentUserSlice.reducer
